import { Curve } from "@/components/Curve";
import { Helmet, renderMeta } from "@/components/Helmet";
import PageRouter from "@/components/PageRouter";
import PageContainer from "@/containers/PageContainer";
import { PageProps, PreviewDataType } from "@/lib/types";
import { addApolloState, initializeApollo } from "@neonjungle/headless";
import type { PageQuery, PageQueryVariables } from "graphql/generated-types";
import { AllPagesDocument, PageDocument, PageInterface } from "graphql/generated-types";
import { GetStaticPaths, GetStaticProps } from "next";
import { useMeasure } from "react-use";

export const getStaticPaths: GetStaticPaths = async () => {
	const apolloClient = initializeApollo();
	const { data } = await apolloClient.query({ query: AllPagesDocument });

	const paths = data?.pages
		.filter((p: PageInterface) => p.live)
		.filter((p: PageInterface) => p.pageType !== "HomePage")
		.map((page: PageInterface) => {
			return {
				params: {
					urlPaths: page.urlPath.split("/").filter((s: string, i: number) => {
						if (i > 1 && s) {
							return s;
						}
					}),
				},
			};
		});

	return {
		paths: paths,
		fallback: "blocking",
	};
};

export const getStaticProps: GetStaticProps = async (context) => {
	const apolloClient = initializeApollo();
	const { previewData } = context as { previewData: PreviewDataType };

	let path = context.params?.urlPaths;
	path = `/${path instanceof Array ? path.join("/") : path}/`;

	const queryVariables: PageQueryVariables = {
		urlPath: path,
		token: previewData?.previewToken,
		contentType: previewData?.contentType,
	};

	const { data } = await apolloClient.query({
		query: PageDocument,
		variables: queryVariables,
	});

	if (!data?.page) {
		return {
			notFound: true,
		};
	}

	const pageProps = addApolloState(apolloClient, {
		props: {
			data,
		},
	}) as PageProps;

	pageProps.revalidate = 10;

	return pageProps;
};

const CMSPage = ({ data }: { data: PageQuery }) => {
	const [ref, { height }] = useMeasure<HTMLElement>();
	const { pageType } = data.page!;

	const curveSelector = () => {
		switch (pageType) {
			case "ContentPage":
			case "ProjectPage":
			case "NewsPage":
				return <ContentPageCurves mainHeight={height} />;
			case "TeamPage":
				return <TeamPageCurves mainHeight={height} />;
			case "ProjectListingPage":
				return <ProjectListingPageCurves mainHeight={height} />;
			case "NewsIndex":
				return <NewsIndexCurves mainHeight={height} />;
			default:
				return <ContentPageCurves mainHeight={height} />;
		}
	};

	return (
		<PageContainer
			mainRef={ref}
			mainElementClasses={`flex flex-col gap-20 overflow-hidden ${pageType === "ContentPage" ? "lg:pb-64" : ""}`}
			script={data.analyticsSettings?.script}
		>
			<Helmet>
				{renderMeta(
					// @ts-ignore
					data.page!.searchImage?.rendition?.url,
					data.page!.seoTitle || data.page!.title,
					` | ${data.sites[0].siteName}`,
					data.page!.searchDescription as string,
					data.sites[0].siteName,
					data.sites[0].hostname,
				)}
			</Helmet>
			<PageRouter data={data} />
			{curveSelector()}
		</PageContainer>
	);
};

const ContentPageCurves = ({ mainHeight }: { mainHeight: number }) => {
	return (
		<>
			<Curve className="absolute left-0 top-[6.5rem] -z-20 lg:top-12" from={90} to={0} />
			{mainHeight > 1500 && <Curve className="absolute bottom-0 left-0 -z-20" from={-180} to={-90} />}
		</>
	);
};

const TeamPageCurves = ({ mainHeight }: { mainHeight: number }) => {
	return (
		<>
			<Curve className="absolute left-0 top-[6.5rem] -z-20 lg:top-12" from={90} to={0} />
			{mainHeight > 1500 && <Curve className="absolute bottom-0 right-0 -z-20 lg:hidden" from={-90} to={-180} />}
		</>
	);
};

const ProjectListingPageCurves = ({ mainHeight }: { mainHeight: number }) => {
	return (
		<>
			<Curve className="absolute left-0 top-[6.5rem] -z-20 lg:top-12" from={90} to={0} />
			{mainHeight > 1500 && <Curve className="absolute bottom-0 right-0 -z-20" from={-90} to={-180} />}
		</>
	);
};

const NewsIndexCurves = ({ mainHeight }: { mainHeight: number }) => {
	return (
		<>
			<Curve className="absolute left-0 top-[6.5rem] -z-20 lg:top-12" from={90} to={0} />
			{mainHeight > 1500 && <Curve className="absolute bottom-0 right-0 -z-20 lg:hidden" from={-90} to={-180} />}
		</>
	);
};

export default CMSPage;
